import { useState, useEffect } from "react";
import { Flame, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

// ⚙️ CONFIG: Update these values as needed
const CONFIG = {
  cohortStartDate: "2026-09-25T00:00:00Z", // Cohort enrollment deadline
  totalSpots: 13,
  spotsRemaining: 7, // Update this manually as spots fill
  foundingPrice: 2470, // 25% off $3,300 for founding members
  regularPrice: 3300,
};

const CountdownBanner = ({ onOpenBooking }) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const target = new Date(CONFIG.cohortStartDate).getTime();
      const diff = target - now;

      if (diff <= 0) {
        setIsVisible(false);
        clearInterval(timer);
        return;
      }

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / 1000 / 60) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  if (!isVisible) return null;

  return (
    <section
      data-testid="countdown-section"
      className="relative py-16 md:py-20 px-6 md:px-12 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #2C3524 0%, #3F4B34 100%)",
      }}
    >
      {/* Decorative gradient */}
      <div
        className="absolute top-0 right-0 w-full h-full opacity-15 pointer-events-none"
        style={{
          background: "radial-gradient(circle at 80% 50%, #C5A059 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          {/* Left: Message */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C5A059]/20 border border-[#C5A059]/40 mb-6">
              <Flame className="w-4 h-4 text-[#DCC286]" strokeWidth={2} />
              <span className="text-[#DCC286] text-xs tracking-[0.15em] uppercase font-medium">
                {t("countdown.badge")}
              </span>
            </div>

            <h2 className="font-heading text-3xl md:text-5xl text-white leading-[1.1] mb-4">
              {t("countdown.titleBefore")}{" "}
              <span className="text-[#DCC286] italic">
                {t("countdown.titleSpots", { count: CONFIG.spotsRemaining })}
              </span>{" "}
              {t("countdown.titleAfter")}
            </h2>

            <p className="text-white/80 text-base md:text-lg font-light leading-relaxed mb-6">
              {t("countdown.body_part1")}
              <strong className="text-white">{t("countdown.body_program")}</strong>
              {t("countdown.body_part2")}
              <span className="text-[#DCC286]">${CONFIG.foundingPrice.toLocaleString()}</span>
              {t("countdown.body_priceSuffix", { regular: CONFIG.regularPrice.toLocaleString() })}
            </p>

            {/* Progress bar */}
            <div className="mb-8">
              <div className="flex justify-between text-xs text-white/60 tracking-wider uppercase mb-2">
                <span>{t("countdown.enrolled", { count: CONFIG.totalSpots - CONFIG.spotsRemaining })}</span>
                <span>{t("countdown.remaining", { count: CONFIG.spotsRemaining })}</span>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#C5A059] to-[#DCC286] transition-all duration-1000"
                  style={{
                    width: `${((CONFIG.totalSpots - CONFIG.spotsRemaining) / CONFIG.totalSpots) * 100}%`,
                  }}
                />
              </div>
            </div>

            <button
              onClick={onOpenBooking}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#C5A059] text-white font-medium hover:bg-[#DCC286] transition-all group"
              data-testid="cohort-cta"
            >
              {t("countdown.cta")}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>

          {/* Right: Countdown */}
          <div className="flex flex-col items-center md:items-end">
            <p className="text-[#DCC286] text-xs tracking-[0.2em] uppercase mb-4">
              {t("countdown.closesIn")}
            </p>
            <div className="grid grid-cols-4 gap-3 md:gap-4">
              {[
                { label: t("countdown.days"), value: timeLeft.days },
                { label: t("countdown.hours"), value: timeLeft.hours },
                { label: t("countdown.mins"), value: timeLeft.minutes },
                { label: t("countdown.secs"), value: timeLeft.seconds },
              ].map((unit, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-16 md:w-20 h-16 md:h-20 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                    <span className="font-heading text-2xl md:text-4xl text-white tabular-nums">
                      {String(unit.value).padStart(2, "0")}
                    </span>
                  </div>
                  <span className="text-white/60 text-xs tracking-[0.15em] uppercase mt-2">
                    {unit.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CountdownBanner;
